import React, {
  FC, useEffect, useState,
} from 'react';
import { connect } from 'react-redux';
import { addAssetUpdates } from '../../../../state/editMetadata';
import AssetFieldComponent from './AssetField';
import { IAppState } from '../../../../state/reducers';
import { ISecurityProfile } from '../../../../api/vault/securityProfiles';
import {
  addLookupValues, ILookupOption, removeLookupValues, selectLookupOptions,
} from '../../../../state/lookups';
import { ILookupAddValue } from '../../../../api/vault/lookups';
import { IAssetAttachment } from '../../../Exchange/UploadDialog/Upload/component';
import { splitLast } from '../../../../utils';
import { readFileContent } from '../../../../utils/fileReader';
import toast from '../../../../utils/toast';
import { additionalAttachments, isAttachmentFileSizeValid } from '../../../../constants/exchange';
import { canUploadAdditionalAttachments, isLacrosse } from '../../../../utils/exchange';
import { securityProfileUpdate } from '../../../../utils/assetDetails/securityProfileUpdate';

interface IOwnProps {
  assetType: string;
  value: any;
  label: string;
  disabled: boolean;
  dataType: string;
  tagName: string;
  lookupObjectId: string;
  track: any;
  isBulkEdit: boolean;
  allowAddRemove: boolean;
  handleChangeCallback?: (update: {}) => void;
  handleConfirm?: (title: string, message: string, callback: (result: boolean) => void) => void;
  // VV-315: profile ids on the asset that the Security field can't show (hidden from this user,
  // or not among its options). The field works in names, so these are added back on save
  // rather than dropped with the names it could not show.
  keepSecurityProfileIds?: string[];
}

interface IStateProps {
  securityProfiles: ISecurityProfile[],
  lookupOptions: ILookupOption[],
  exchangeSport: string;
}

interface IDispatchProps {
  addAssetUpdates: (update: {}) => void;
  addLookupValues: (lookupObjectId: string, updateLookup: ILookupAddValue[]) => void;
  removeLookupValues: (lookupObjectId: string, names: string[]) => void;
}

interface IProps extends IOwnProps, IStateProps, IDispatchProps { }

const tagNamesWithoutAddRemove = ['amazonRssFeed', 'photoRotation'];

const namesToSpIds = (securityProfiles: ISecurityProfile[], spNames: string[]) => securityProfiles
  .filter((sp) => spNames.some((name) => sp.name === name))
  .map((sp) => sp._id);

const AssetFieldWrapper: FC<IProps> = (props: IProps) => {
  const [stateValue, setStateValue] = useState<any | null>(props.value);
  const {
    assetType,
    tagName,
    dataType,
    label,
    value,
    lookupObjectId,
    disabled,
    securityProfiles,
    lookupOptions,
    allowAddRemove,
    exchangeSport,
    addAssetUpdates,
    handleChangeCallback,
    addLookupValues,
    removeLookupValues,
    handleConfirm,
    keepSecurityProfileIds = [],
  } = props;

  useEffect(() => {
    if (value !== stateValue) setStateValue(value);
  }, [value]);

  const handleRemoveAttachment = (attachment: IAssetAttachment) => {
    const newValue = stateValue.filter((a: IAssetAttachment) => a.sourceFileName !== attachment.sourceFileName);
    setStateValue(newValue);
    addAssetUpdates({ [tagName]: newValue });
  };

  const handleDropAttachments = async (attachments: File[], exchangeFile: IAssetAttachment) => {
    let failedMessage: string;

    // Check if the file is a valid attachment
    attachments.forEach((attachment) => {
      const [attachmentName, attachmentType] = splitLast(attachment.name, '.');
      if (!canUploadAdditionalAttachments(exchangeSport) && attachmentType !== 'xchange') {
        failedMessage = `Attachment Upload Failed - Invalid attachment type: ${attachment.name}`;
      } else if (canUploadAdditionalAttachments(exchangeSport) && !additionalAttachments(exchangeSport).includes(attachmentType)) {
        failedMessage = `Attachment Upload Failed - Invalid attachment type: ${attachment.name}`;
      }

      if (!isAttachmentFileSizeValid(attachment)) {
        failedMessage = `Attachment Upload Failed - File size exceeds 25MB: ${attachment.name}`;
      } else if (attachment.size === 0) {
        failedMessage = `Attachment Upload Failed - File is empty: ${attachment.name}`;
      }
    });

    if (!failedMessage) {
      const newAttachments: IAssetAttachment[] = await Promise.all(
        attachments.map(async (attachment) => {
          if (failedMessage) return null;
          const [attachmentName, attachmentType] = splitLast(attachment.name, '.');
          const content = await readFileContent(attachment);
          if (!content) {
            failedMessage = `Attachment  Upload Failed - Error reading file: ${attachment.name}`;
            return null;
          }
          return {
            sourceFileName: attachmentType === 'xchange' ? exchangeFile.sourceFileName : attachment.name,
            attachmentName,
            attachmentType,
            content,
            newFile: true,
          };
        }),
      );

      newAttachments.forEach((attachment) => {
        const foundIndex = stateValue.findIndex((a: IAssetAttachment) => a.sourceFileName === attachment?.sourceFileName);
        if (foundIndex !== -1) {
          stateValue[foundIndex] = attachment;
        } else {
          stateValue.push(attachment);
        }
      });
    }

    if (failedMessage) {
      toast(failedMessage, { type: toast.TYPE.ERROR });
    } else {
      setStateValue(stateValue);
      addAssetUpdates({ [tagName]: stateValue });
    }
  };

  const handleValueUpdate = (value: any) => {
    let newValue = value;
    if (dataType === 'boolean') {
      newValue = newValue.toLowerCase() === 'true';
    } else if (tagName === 'securityProfiles') {
      newValue = securityProfileUpdate(newValue, securityProfiles, keepSecurityProfileIds);
    } else if (tagName === 'owner') {
      [newValue] = namesToSpIds(securityProfiles, [newValue]);
    }

    if (handleChangeCallback) {
      handleChangeCallback({ [tagName]: newValue });
    } else {
      addAssetUpdates({ [tagName]: newValue });
    }
  };

  const handleDeleteOption = (option: string) => {
    if (handleConfirm) {
      handleConfirm('Delete Existing Lookup Value', `'${option}' from '${label}'`, (result) => {
        if (result) {
          removeLookupValue(option);
          if (stateValue === option) {
            setStateValue('');
            addAssetUpdates({ [tagName]: '' });
          }
        }
      });
    }
  };

  const handleAddOption = (option: string) => {
    if (handleConfirm) {
      handleConfirm('Add New Lookup Value', `'${option}' to '${label}'`, (result) => {
        if (result) addNewLookupValue(option);
        else setStateValue('');
      });
    }
  };

  const handleBlur = () => handleValueUpdate(stateValue);
  const addNewLookupValue = (lookupValue: string) => addLookupValues(lookupObjectId, [{ name: lookupValue, description: lookupValue }]);
  const removeLookupValue = (lookupValue: string) => removeLookupValues(lookupObjectId, [lookupValue]);

  const handleChange = (value: any, newValue: boolean = false) => {
    const isTextInput = (dataType !== 'date' && !(dataType === 'list' && typeof value === 'object')
      && tagName !== 'owner' && !lookupObjectId);

    // An asset must keep at least one profile. Hidden ones (keepSecurityProfileIds) count: they
    // are added back on save, so clearing the visible ones still leaves the asset with some.
    if (tagName === 'securityProfiles' && !value.length && !keepSecurityProfileIds.length) return;
    if (tagName === 'owner' && (!value.length || value === '')) return;
    setStateValue(value);
    if (!isTextInput) handleValueUpdate(value);
    if (newValue) handleAddOption(value);
  };

  return (
    <AssetFieldComponent
      data-auto="Asset Details"
      assetType={assetType}
      tagName={tagName}
      dataType={dataType}
      label={label}
      labelValue={label.replace(/\s/g, '')}
      value={stateValue}
      lookupObjectId={lookupObjectId}
      lookupOptions={lookupOptions}
      disabled={disabled}
      allowAddRemove={!tagNamesWithoutAddRemove.includes(tagName) && allowAddRemove}
      onChange={(e: React.ChangeEvent<HTMLInputElement>, newType?: boolean) => { handleChange(e.target.value, newType); }}
      onDateChange={handleChange}
      onBlur={handleBlur}
      onDeleteOption={(option: any) => { handleDeleteOption(option); }}
      onHandleDropAttachments={handleDropAttachments}
      onHandleDeleteAttachment={handleRemoveAttachment}
    />
  );
};

const mapStateToProps = (state: IAppState, ownProps: IOwnProps): IStateProps => ({
  securityProfiles: state.securityProfiles?.idValueMapping ?? [],
  lookupOptions: selectLookupOptions(ownProps.lookupObjectId, state.lookups),
  exchangeSport: state.exchange?.initExchange?.sport,
});

const mapDispatchToProps: IDispatchProps = {
  addAssetUpdates,
  addLookupValues,
  removeLookupValues,
};

export default connect(mapStateToProps, mapDispatchToProps)(AssetFieldWrapper);
